import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
  Subscription,
  SubscriptionPlan,
  SubscriptionContextType,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555';

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Derived state
  const plan: SubscriptionPlan = (subscription?.plan as SubscriptionPlan) || 'free';
  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing';

  // Fetch subscription data
  const fetchSubscription = useCallback(async () => {
    if (!currentUser) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const response = await fetch(`${API_BASE_URL}/api/subscriptions/${currentUser.uid}`);

      if (!response.ok) {
        throw new Error('Failed to fetch subscription');
      }

      const data = await response.json();

      // Convert date strings to Date objects
      setSubscription({
        ...data,
        currentPeriodStart: data.currentPeriodStart ? new Date(data.currentPeriodStart) : new Date(),
        currentPeriodEnd: data.currentPeriodEnd ? new Date(data.currentPeriodEnd) : new Date(),
        lastRecordingDate: data.lastRecordingDate ? new Date(data.lastRecordingDate) : null,
        createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
        updatedAt: data.updatedAt ? new Date(data.updatedAt) : new Date(),
      });
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Initialize subscription on auth change
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Create Stripe checkout session
  const createStripeCheckout = useCallback(async (
    successUrl: string,
    cancelUrl: string
  ): Promise<string> => {
    if (!currentUser || !currentUser.email) {
      throw new Error('User must be logged in');
    }

    const response = await fetch(`${API_BASE_URL}/api/payments/stripe/create-checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        plan: 'kids',
        billingCycle: 'monthly',
        userId: currentUser.uid,
        userEmail: currentUser.email,
        successUrl,
        cancelUrl,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create checkout session');
    }

    const { url } = await response.json();
    return url;
  }, [currentUser]);

  // Create Stripe customer portal session
  const createStripePortal = useCallback(async (returnUrl: string): Promise<string> => {
    if (!subscription?.stripeCustomerId) {
      throw new Error('No Stripe customer found');
    }

    const response = await fetch(`${API_BASE_URL}/api/payments/stripe/create-portal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customerId: subscription.stripeCustomerId,
        returnUrl,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create portal session');
    }

    const { url } = await response.json();
    return url;
  }, [subscription]);

  // Refresh subscription data
  const refreshSubscription = useCallback(async (): Promise<void> => {
    setLoading(true);
    await fetchSubscription();
  }, [fetchSubscription]);

  // Initialize subscription for new user
  const initializeSubscription = useCallback(async (): Promise<void> => {
    if (!currentUser || !currentUser.email) {
      throw new Error('User must be logged in');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/subscriptions/${currentUser.uid}/initialize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: currentUser.email,
          region: 'na',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to initialize subscription');
      }

      await fetchSubscription();
    } catch (err) {
      console.error('Error initializing subscription:', err);
      throw err;
    }
  }, [currentUser, fetchSubscription]);

  const value: SubscriptionContextType = {
    subscription,
    plan,
    isActive,
    loading,
    error,
    createStripeCheckout,
    createStripePortal,
    refreshSubscription,
    initializeSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
